/// <amd-module name="FeedTest/Utils/Fakes/FakeGlobalSettingsService" />
import { IIdentity } from "VSS/Features/Identities/IdentityService";
import { IdentityDescriptor } from "VSS/Platform/Contracts/Identities";

import { IFeedGlobalSettingsService, INormalizedGlobalPermission, IRoleAssignment } from "Feed/Admin/Types";
import { GlobalPermission, GlobalRole } from "Feed/Common/Contracts/Feed";

/**
 * An in-memory settings service
 */
export class FakeGlobalSettingsService implements IFeedGlobalSettingsService {
    public permissions: GlobalPermission[] = [];
    public identities: IIdentity[] = [];

    constructor(public pcaId: string = "pcaIdentity", public everyoneGroupid: string = "everyoneGId") {
        // Add project collection administrator
        this.permissions.push({
            identityId: pcaId,
            identityDescriptor: { identifier: pcaId, identityType: "test" },
            role: GlobalRole.Administrator
        });

        // Add everyone group
        this.permissions.push({
            identityId: everyoneGroupid,
            identityDescriptor: { identifier: everyoneGroupid, identityType: "test" },
            role: GlobalRole.None
        });
    }

    /* get the id of the ProjectCollectionAdministrators group */
    public getProjectCollectionAdminId = () => this.pcaId;

    /* get the id of the Everyone group */
    public getEveryoneGroupId = () => this.everyoneGroupid;

    /* Get the feed permission data for this organization.
    Does not contain the administrator permissions, or identities with no permissions. */
    public getPermissionDataAsync(): Promise<INormalizedGlobalPermission[]> {
        const normalizedPermissions = this.permissions
            .filter(p => p.identityId !== this.pcaId && p.role != GlobalRole.None)
            .map(p => {
                const { identityId, role, identityDescriptor } = p;
                let descriptor: IdentityDescriptor | string = identityDescriptor;

                if (typeof descriptor !== "string") {
                    descriptor = `${descriptor.identityType};${descriptor.identifier}`;
                }

                return { identityId, role, identityDescriptor: descriptor } as INormalizedGlobalPermission;
            });

        return Promise.resolve(normalizedPermissions);
    }

    /* Modify feed permissions */
    public savePermissionDataAsync(modifiedRoles: IRoleAssignment[]): Promise<void> {
        modifiedRoles.forEach(role => {
            const permission = this.permissions.find(p => p.identityId === role.tfid);
            if (permission) {
                permission.role = role.role;
            } else {
                this.permissions.push({
                    identityId: role.tfid,
                    identityDescriptor: { identifier: role.tfid, identityType: "test" },
                    role: role.role
                });
            }
        });

        return Promise.resolve();
    }

    // returns map of tfid -> IIdentity
    public getIIdentityFromPermissionAsync(
        permissions: INormalizedGlobalPermission[]
    ): Promise<Map<string, IIdentity>> {
        const map = new Map<string, IIdentity>();

        for (const permission of permissions) {
            const identity = this.identities.find(i => i.localId == permission.identityId);
            if (identity) {
                map.set(permission.identityId, identity);
            }
        }

        return Promise.resolve(map);
    }
}
